import React, { useState, useRef, useEffect } from 'react';
import { func, string } from 'prop-types';
import classNames from 'classnames';

import { IconCheckmark, IconClose } from '../../components';
import css from './CustomAmenityInput.module.css';

const CustomAmenityInput = props => {
  const { onSubmit, onCancel, className, rootClassName } = props;

  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleInputChange = e => {
    setInputValue(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = () => {
    const trimmedValue = inputValue.trim();
    
    if (!trimmedValue) {
      setError('Please enter an amenity name');
      return;
    }

    onSubmit(trimmedValue);
    setInputValue('');
    setError('');
  };

  const handleCancel = () => {
    setInputValue('');
    setError('');
    onCancel();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.inputContainer}>
        <input
          ref={inputRef}
          type="text"
          className={classNames(css.input, { [css.inputError]: !!error })}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Enter custom amenity name"
          aria-label="Custom amenity name"
          aria-invalid={!!error}
          aria-describedby={error ? 'custom-amenity-error' : undefined}
        />
        <div className={css.buttonContainer}>
          <button
            type="button"
            className={css.confirmButton}
            onClick={handleSubmit}
            aria-label="Confirm custom amenity"
          >
            <IconCheckmark size="small" />
          </button>
          <button
            type="button"
            className={css.cancelButton}
            onClick={handleCancel}
            aria-label="Cancel custom amenity"
          >
            <IconClose size="small" />
          </button>
        </div>
      </div>
      {error && (
        <div id="custom-amenity-error" className={css.error} role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

CustomAmenityInput.defaultProps = {
  className: null,
  rootClassName: null,
};

CustomAmenityInput.propTypes = {
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  className: string,
  rootClassName: string,
};

export default CustomAmenityInput;
